/**
 * SessionRail — left pane of the AgentWindow.
 *
 * Kimi/Cowork-style split: lightweight "Chats" (chat_sessions) and persistent
 * "Work sessions" (cortex_sessions), grouped by organization project
 * (Cursor-style). Selecting a row switches the engine's session; the active
 * session shows a live run-state chip.
 */

import { useMemo, useState } from "react";
import {
  MessageSquare,
  Layers,
  Plus,
  ChevronDown,
  ChevronRight,
  Folder,
  Loader2,
  HelpCircle,
  Inbox,
} from "lucide-react";
import { cn } from "@flowstate/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import type { AgentSessionSummary, SessionKind } from "../engine/sessionStores";
import type { TimelineRun } from "../engine/cortexStreamReducer";

interface ProjectLike {
  id: string;
  name?: string | null;
  title?: string | null;
}

interface SessionRailProps {
  chats: AgentSessionSummary[];
  work: AgentSessionSummary[];
  projects: ProjectLike[];
  activeKind: SessionKind;
  activeId: string | undefined;
  /** Live run states for the active session (status chips). */
  runs: TimelineRun[];
  onSelect: (kind: SessionKind, id: string) => void;
  onNew: (kind: SessionKind) => void;
  className?: string;
}

interface Grouped {
  projectId: string | null;
  projectName: string;
  chats: AgentSessionSummary[];
  work: AgentSessionSummary[];
}

function projectNameFor(id: string | null | undefined, projects: ProjectLike[]) {
  const p = projects.find((x) => x.id === id);
  return p?.name ?? p?.title ?? "Project";
}

function groupByProject(
  chats: AgentSessionSummary[],
  work: AgentSessionSummary[],
  projects: ProjectLike[],
): Grouped[] {
  const map = new Map<string | null, Grouped>();
  const ensure = (pid: string | null | undefined) => {
    const key = pid ?? null;
    if (!map.has(key)) {
      map.set(key, {
        projectId: key,
        projectName: key ? projectNameFor(key, projects) : "Unassigned",
        chats: [],
        work: [],
      });
    }
    return map.get(key)!;
  };
  for (const c of chats) ensure(c.projectId).chats.push(c);
  for (const w of work) ensure(w.projectId).work.push(w);

  return [...map.values()].sort((a, b) => {
    if (a.projectId === null) return 1;
    if (b.projectId === null) return -1;
    return a.projectName.localeCompare(b.projectName);
  });
}

function RunChip({ runs }: { runs: TimelineRun[] }) {
  const awaitingInput = runs.find((r) => r.lifecycle === "awaiting_input");
  const awaiting = runs.find((r) => r.lifecycle === "awaiting");
  const running = runs.find((r) => r.lifecycle === "running");
  if (awaitingInput) {
    return (
      <span className="flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary shrink-0">
        <HelpCircle className="h-2.5 w-2.5" />
        input
      </span>
    );
  }
  if (awaiting) {
    return (
      <span className="flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 shrink-0">
        <Loader2 className="h-2.5 w-2.5 animate-spin" />
        waiting
      </span>
    );
  }
  if (running) {
    return (
      <span className="flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-green-500/15 text-green-600 shrink-0">
        <Loader2 className="h-2.5 w-2.5 animate-spin" />
        live
      </span>
    );
  }
  return null;
}

export function SessionRail({
  chats,
  work,
  projects,
  activeKind,
  activeId,
  runs,
  onSelect,
  onNew,
  className,
}: SessionRailProps) {
  const groups = useMemo(() => groupByProject(chats, work, projects), [chats, work, projects]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const toggle = (key: string) => setCollapsed((p) => ({ ...p, [key]: !p[key] }));

  const row = (s: AgentSessionSummary, isWork: boolean) => {
    const isActive = activeId === s.id && activeKind === s.kind;
    const Icon = isWork ? Layers : MessageSquare;
    return (
      <button
        key={`${s.kind}-${s.id}`}
        type="button"
        onClick={() => onSelect(s.kind, s.id)}
        className={cn(
          "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-xs transition-colors",
          isActive
            ? "bg-primary/10 text-foreground"
            : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
        )}
      >
        <Icon className="h-3.5 w-3.5 shrink-0 opacity-60" />
        <span className="flex-1 truncate">{s.title}</span>
        {isActive && <RunChip runs={runs} />}
      </button>
    );
  };

  return (
    <div
      className={cn(
        "flex flex-col h-full min-h-0 border-r border-border/30 bg-background/40",
        className,
      )}
    >
      <div className="px-3 pt-3 pb-1 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          Sessions
        </span>
      </div>

      <div className="flex gap-1.5 px-3 pb-2">
        <Button
          variant="outline"
          size="sm"
          className="h-7 flex-1 text-[11px] gap-1"
          onClick={() => onNew("chat")}
        >
          <Plus className="h-3 w-3" /> Chat
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-7 flex-1 text-[11px] gap-1"
          onClick={() => onNew("work")}
        >
          <Plus className="h-3 w-3" /> Work
        </Button>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="px-2 pb-3 space-y-2">
          {groups.map((g) => {
            const key = g.projectId ?? "__unassigned";
            const isCollapsed = !!collapsed[key];
            return (
              <div key={key}>
                <button
                  type="button"
                  onClick={() => toggle(key)}
                  className="w-full flex items-center gap-1.5 px-1 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {isCollapsed ? (
                    <ChevronRight className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                  <Folder className="h-3 w-3 opacity-60" />
                  <span className="truncate">{g.projectName}</span>
                  <span className="ml-auto text-[9px] text-muted-foreground/50">
                    {g.chats.length + g.work.length}
                  </span>
                </button>
                {!isCollapsed && (
                  <div className="ml-3 space-y-0.5">
                    {g.work.map((s) => row(s, true))}
                    {g.chats.map((s) => row(s, false))}
                  </div>
                )}
              </div>
            );
          })}

          {groups.length === 0 && (
            <div className="px-3 py-8 text-center">
              <Inbox className="h-6 w-6 mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-xs text-muted-foreground">
                No sessions yet — start a chat or a work session.
              </p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
